/**
 * Machine status in plain words, and the one colour map for it (#804).
 *
 * Deployments.tsx and NodesPanel.tsx each had their own badge map, and they
 * disagreed for `assigned`, `cordoned` and `upgrading`. Both now render
 * through `describeMachineStatus`.
 *
 * Labels follow the stage names in the install docs (docs.jeket.com,
 * "Adding boxes"); the raw status code stays available as `code` for the
 * badge tooltip and the docs' badge table.
 */
import type { MachineStatus } from '@/types';

/**
 * - healthy:   the box is doing its job
 * - progress:  moving through setup on its own; nothing to do yet
 * - attention: waiting on something outside the box (a person, a config, a boot)
 * - error:     stopped; needs recovery or support
 * - muted:     not part of a running site
 */
export type StatusTone = 'healthy' | 'progress' | 'attention' | 'error' | 'muted';

export interface MachineStatusDescription {
  /** The raw status, e.g. `config_apply_failed`. */
  code: string;
  /** Short plain-words label for the badge. */
  label: string;
  /** One sentence on what the status means for the customer. */
  description: string;
  tone: StatusTone;
}

const DESCRIPTIONS: Record<MachineStatus, Omit<MachineStatusDescription, 'code'>> = {
  registered: {
    label: 'Not assigned',
    description: 'Known to Jeket but not assigned to a site.',
    tone: 'muted',
  },
  assigned: {
    label: 'Waiting to boot',
    description: 'Assigned to this site; waiting for the box to boot the site install image.',
    tone: 'attention',
  },
  discovering: {
    label: 'Reading hardware',
    description: 'The box booted the site image and joined over the management tunnel. Jeket is reading its hardware.',
    tone: 'progress',
  },
  pending_enrollment: {
    label: 'Waiting for its configuration',
    description: 'Jeket is matching the box to a certified hardware profile before sending its configuration.',
    tone: 'attention',
  },
  config_pending: {
    label: 'Configuration ready',
    description: 'The configuration is prepared and waiting to be deployed to the box.',
    tone: 'attention',
  },
  config_pushed: {
    label: 'Installing',
    description: 'The box received its configuration and is installing Talos Linux onto its system drive.',
    tone: 'progress',
  },
  configuring: {
    label: 'Installing',
    description: 'The box received its configuration and is installing Talos Linux onto its system drive.',
    tone: 'progress',
  },
  config_apply_failed: {
    label: 'Install failed',
    description: "The box took its configuration but didn't come back within 5 minutes.",
    tone: 'error',
  },
  bootstrapping: {
    label: 'Starting the cluster',
    description: "The first control-plane box is starting the site's Kubernetes cluster.",
    tone: 'progress',
  },
  joining: {
    label: 'Joining the cluster',
    description: 'The box is registering with the cluster and waiting for Kubernetes to report it Ready.',
    tone: 'progress',
  },
  installing_prereqs: {
    label: 'Installing platform components',
    description: 'The site is installing what JBS depends on: storage, ingress, load balancer, GPU and dataplane networking.',
    tone: 'progress',
  },
  ready: {
    label: 'Ready',
    description: 'Kubernetes reports the box Ready.',
    tone: 'healthy',
  },
  cordoned: {
    label: 'Cordoned',
    description: 'Running, but marked so no new workloads are scheduled onto it.',
    tone: 'attention',
  },
  upgrading: {
    label: 'Upgrading',
    description: 'Talos is being upgraded on this box; it restarts during the upgrade.',
    tone: 'progress',
  },
  unsupported_hardware: {
    label: 'Unsupported hardware',
    description: 'No certified hardware profile matches this box, so Jeket will not configure it.',
    tone: 'error',
  },
  error: {
    label: 'Error',
    description: 'The box stopped in an error state. The message below names the cause.',
    tone: 'error',
  },
  decommissioned: {
    label: 'Decommissioned',
    description: 'Removed from service.',
    tone: 'muted',
  },
};

/**
 * Describe a status. Accepts any string: a status this build doesn't know
 * (a newer deployment-service can add one first) renders as its humanised
 * code rather than an empty, colourless badge.
 */
export function describeMachineStatus(status: MachineStatus | string): MachineStatusDescription {
  const known = (DESCRIPTIONS as Record<string, Omit<MachineStatusDescription, 'code'>>)[status];
  if (known) return { code: status, ...known };
  const words = String(status || 'unknown').replace(/_/g, ' ');
  return {
    code: status,
    label: words.charAt(0).toUpperCase() + words.slice(1),
    description: `Status "${status}" is not known to this version of the console.`,
    tone: 'muted',
  };
}

export const STATUS_TONE_CLASSES: Record<StatusTone, string> = {
  healthy: 'bg-status-healthy/20 text-status-healthy',
  progress: 'bg-status-warning/20 text-status-warning',
  attention: 'bg-gold/20 text-gold',
  error: 'bg-status-error/20 text-status-error',
  muted: 'bg-silver-muted/20 text-silver-muted',
};
